import { FC, useEffect, useMemo, useRef } from "react";

import { Box, Stack } from "@mui/material";
import {
  CommunicationPreferenceFormType,
  FormType,
  Locales,
  QuestionType,
  TicketingFormCategory,
} from "@simplyk/common";
import { FormProvider, useForm } from "react-hook-form";
import { v4 } from "uuid";

import { FundDesignationFormContent } from "../FundDesignationFormContent";
import { useFundSelectionForm } from "../hooks/useFundSelectionForm";
import { ScopeTogglePills } from "../ScopeTogglePills/ScopeTogglePills";
import { CreateQuestionFormData, CustomPropertyOption, QuestionScope, RateOption, TicketingChoiceV3 } from "../types";
import { RateAssignmentSection } from "./RateAssignmentSection";
import {
  QuestionTypeConfig,
  SelectableQuestionType,
  getQuestionTypeConfig,
  getQuestionTypeRendererConfig,
} from "../helpers/questionTypeIcons";
import { resolveMatchingCustomProperty } from "../helpers/resolveMatchingCustomProperty";

import { ChoicesSection } from "./ChoicesSection/ChoicesSection";
import { useQuestionTypeConfig } from "./hooks/useQuestionTypeConfig";
import { useScopeManager } from "./hooks/useScopeManager";
import {
  ChoiceFormData,
  COMMUNICATION_PREFERENCES_TYPE,
  FormValues,
  FUND_DESIGNATION_TYPE,
  QuestionFormKind,
  QUESTION_TYPES_WITH_CHOICES,
  ResponseFormatType,
} from "./types";

import { CommunicationPreferencesFormContent } from "@/features/LiveFormEditor/Steps/StepQuestionsV4/CommunicationPreferences/CommunicationPreferencesFormContent";
import { useCommunicationPreferencesForm } from "@/features/LiveFormEditor/Steps/StepQuestionsV4/CommunicationPreferences/helpers/useCommunicationPreferencesForm";

import { getLabel, isRequired } from "@/common/helpers/validators";
import { Trans, useTranslate } from "@/common/hooks/useTranslate";
import { CollapseAndFade } from "@/components/Animations/CollapseAndFade";
import { DashboardAdaptiveModal } from "@/dashboard-design-system/AdaptiveModal";
import { DashboardDialog, zeffyDialogClasses } from "@/dashboard-design-system/Dialog";
import { DashboardIcon } from "@/dashboard-design-system/Icon";
import { DashboardInputLabel } from "@/dashboard-design-system/InputLabel";
import { DashboardMenuItem } from "@/dashboard-design-system/MenuItem";
import { DashboardSelect } from "@/dashboard-design-system/Select";
import { DashboardSwitch } from "@/dashboard-design-system/Switch";
import { DashboardTextField } from "@/dashboard-design-system/TextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { Amount, Add, Lists, User } from "@/icons/outlined";

type ChoiceInput = { body: string } | TicketingChoiceV3;

interface CreateQuestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateQuestionFormData) => void;
  isLoading?: boolean;
  isEditing?: boolean;
  defaultValues?: Partial<Omit<CreateQuestionFormData, "choices"> & { choices: ChoiceInput[] }>;
  questionNamesOnForm?: string[];
  matchableProperties?: CustomPropertyOption[];
  showScopeSelector?: boolean;
  showRateAssignment?: boolean;
  showMaximumLimit?: boolean;
  rates?: RateOption[];
  formCategory?: TicketingFormCategory;
  disableNonEditableFields?: boolean;
  countAlreadyAnswered?: number;
  showFundDesignation?: boolean;
  showCommunicationPreferences?: boolean;
  communicationPreferenceFormType?: CommunicationPreferenceFormType;
  onCommunicationPreferencesSaved?: () => void;
  formId?: string;
  formType?: FormType;
  locale?: Locales;
  formKind: QuestionFormKind;
  canAddAttendeeName?: boolean;
  // Set 0 while swapping from AddQuestionModal so the shared backdrop doesn't flicker.
  transitionDuration?: number;
}

export const CreateQuestionModal: FC<CreateQuestionModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  defaultValues,
  isEditing = false,
  questionNamesOnForm = [],
  matchableProperties = [],
  showScopeSelector = false,
  showRateAssignment = false,
  showMaximumLimit = false,
  rates = [],
  formCategory,
  disableNonEditableFields = false,
  countAlreadyAnswered = 0,
  showFundDesignation = false,
  showCommunicationPreferences = false,
  communicationPreferenceFormType,
  onCommunicationPreferencesSaved,
  formId,
  formType,
  locale = Locales.EN,
  formKind,
  canAddAttendeeName = true,
  transitionDuration,
}) => {
  const { t } = useTranslate();
  const initialScope = defaultValues?.scope ?? "buyer";
  const initialType =
    defaultValues?.type ?? (initialScope === "participant" ? QuestionType.Multiple : QuestionType.Simple);

  const {
    selectedFundIds,
    setSelectedFundIds,
    hasEnoughFundsSelected,
    formHasMoreThanOneFund,
    isSaving: isFundLoading,
    handleSave: handleFundSave,
    resetState: resetFundState,
  } = useFundSelectionForm({
    formId,
    formType,
    locale: locale ?? Locales.EN,
    enabled: showFundDesignation,
    onSaved: onClose,
  });

  const getDefaultChoices = (): ChoiceFormData[] => {
    if (defaultValues?.choices && defaultValues.choices.length > 0) {
      return defaultValues.choices.map((c, idx) => {
        const ticketingChoice = c as TicketingChoiceV3;
        return {
          _key: v4(),
          ...(ticketingChoice.id && { id: ticketingChoice.id }),
          body: c.body,
          sortIndex: idx,
          maximum: ticketingChoice.maximum ?? null,
          remainingCapacity: ticketingChoice.remainingCapacity,
        };
      });
    }
    if (QUESTION_TYPES_WITH_CHOICES.includes(initialType)) {
      return [
        { _key: v4(), body: "", sortIndex: 0, maximum: null },
        { _key: v4(), body: "", sortIndex: 1, maximum: null },
      ];
    }
    return [];
  };

  const form = useForm<FormValues>({
    defaultValues: {
      question: defaultValues?.body ?? "",
      type: initialType,
      required: defaultValues?.required ?? false,
      choices: getDefaultChoices(),
      scope: initialScope,
      askForAllRates: defaultValues?.askForAllRates ?? true,
      ratesAssigned: defaultValues?.ratesAssigned ?? [],
      isMaximumActive: defaultValues?.isMaximumActive ?? false,
    },
  });

  const selectedType = form.watch("type");
  const selectedScope = form.watch("scope");
  const isMaximumActive = form.watch("isMaximumActive");

  const { getDefaultQuestionTitle, handleTypeChange, selectableTypes, isAutoFilledTitle } = useQuestionTypeConfig({
    form,
    selectedScope,
    isEditing,
    showFundDesignation,
    formHasMoreThanOneFund,
    formKind,
    showCommunicationPreferences,
    canAddAttendeeName,
    selectedType,
  });

  const {
    options: communicationPreferenceOptions,
    selectedContactListIds: selectedCommunicationPreferenceIds,
    setSelectedContactListIds: setSelectedCommunicationPreferenceIds,
    questionTitle: communicationPreferencesQuestionTitle,
    defaultQuestionTitle: defaultCommunicationPreferencesQuestionTitle,
    setQuestionTitle: setCommunicationPreferencesQuestionTitle,
    canManageOptions: canManageCommunicationPreferenceOptions,
    isLoading: isLoadingCommunicationPreferences,
    isSaving: isSavingCommunicationPreferences,
    canSave: canSaveCommunicationPreferences,
    handleCreateOption: handleCreateCommunicationPreferenceOption,
    handleRenameOption: handleRenameCommunicationPreferenceOption,
    handleDeleteOption: handleDeleteCommunicationPreferenceOption,
    handleSave: handleCommunicationPreferencesSave,
    ensureDefaultOption: ensureDefaultCommunicationPreferenceOption,
    resetState: resetCommunicationPreferencesState,
  } = useCommunicationPreferencesForm({
    formId,
    formType: communicationPreferenceFormType,
    formLocale: locale,
    enabled: showCommunicationPreferences && isOpen && selectedType === COMMUNICATION_PREFERENCES_TYPE,
    onSaved: () => {
      onCommunicationPreferencesSaved?.();
      onClose();
    },
  });

  const {
    displayedScope,
    pendingScopeChange,
    setPendingScopeChange,
    handleScopeChange,
    commitScopeChange,
    resetScope,
  } = useScopeManager({
    form,
    defaultScope: initialScope,
    isEditing,
    countAlreadyAnswered,
    getDefaultQuestionTitle,
  });

  const typedQuestion = form.watch("question");
  const liveMatchedProperty = useMemo(
    () =>
      isEditing
        ? null
        : resolveMatchingCustomProperty({
            body: typedQuestion,
            scope: selectedScope,
            properties: matchableProperties,
          }),
    [isEditing, typedQuestion, selectedScope, matchableProperties]
  );

  const noDuplicateName = useMemo(
    () => ({
      validate: {
        noDuplicate: (value: string) => {
          if (isEditing && defaultValues?.body?.toLowerCase() === value.toLowerCase()) {
            return true;
          }

          const scope = selectedScope;
          if (resolveMatchingCustomProperty({ body: value, scope, properties: matchableProperties })) {
            return true;
          }

          const isDuplicate = questionNamesOnForm.some((name) => name.toLowerCase() === value.toLowerCase());
          return isDuplicate ? t("dashboard", "customQuestion.duplicateError") : true;
        },
      },
    }),
    [isEditing, defaultValues?.body, questionNamesOnForm, matchableProperties, selectedScope, t]
  );

  const matchedPropertyId = liveMatchedProperty?.id ?? null;
  const isMatchFromAutoFilledTitle = Boolean(liveMatchedProperty) && isAutoFilledTitle(typedQuestion);
  const preMatchValuesRef = useRef<{ type: ResponseFormatType; choices: ChoiceFormData[] } | null>(null);
  const resetForm = () => {
    preMatchValuesRef.current = null;
    form.reset();
  };
  useEffect(() => {
    if (liveMatchedProperty) {
      if (!isMatchFromAutoFilledTitle) {
        preMatchValuesRef.current ??= { type: form.getValues("type"), choices: form.getValues("choices") };
      }
      form.setValue("type", liveMatchedProperty.type);
      form.setValue(
        "choices",
        liveMatchedProperty.choices.map((choice, idx) => ({
          _key: v4(),
          body: choice.body,
          sortIndex: idx,
          maximum: null,
        }))
      );
      return;
    }

    if (preMatchValuesRef.current) {
      form.setValue("type", preMatchValuesRef.current.type);
      form.setValue("choices", preMatchValuesRef.current.choices);
      preMatchValuesRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchedPropertyId]);

  const needsChoices = QUESTION_TYPES_WITH_CHOICES.includes(selectedType as QuestionType) && !liveMatchedProperty;
  const minimumChoicesRequired = needsChoices ? 2 : 1;

  const shouldShowRateAssignment = showRateAssignment && selectedScope === "participant" && rates.length > 1;
  const shouldShowMaxChoices = showMaximumLimit && needsChoices;

  const hasFixedQuestionTitle =
    selectedScope === "participant" &&
    (selectedType === QuestionType.Name ||
      selectedType === QuestionType.Email ||
      selectedType === QuestionType.Address);

  const isSpecialGuestQuestion = Boolean(isEditing) && hasFixedQuestionTitle;

  const handleSubmit = form.handleSubmit((data) => {
    if (data.type === FUND_DESIGNATION_TYPE || data.type === COMMUNICATION_PREFERENCES_TYPE) {
      return;
    }

    const matchedProperty = liveMatchedProperty;

    if (matchedProperty) {
      const result: CreateQuestionFormData = {
        body: matchedProperty.body,
        type: matchedProperty.type,
        required: data.required,
        choices: matchedProperty.choices.map((choice) => ({ body: choice.body })),
        matchedCustomProperty: matchedProperty,
      };

      if (formKind === "ticketing") {
        result.scope = data.scope;
        result.askForAllRates = data.askForAllRates;
        result.ratesAssigned = data.ratesAssigned;
      }

      onSubmit(result);
      resetForm();
      resetScope();
      return;
    }

    const cleanedChoices = needsChoices
      ? data.choices.filter((c) => c.body.trim() !== "").map((c) => ({ id: c.id, body: c.body }))
      : [];

    const choicesWithMaximum =
      showMaximumLimit && needsChoices
        ? data.choices
            .filter((c) => c.body.trim() !== "")
            .map((c) => ({
              id: c.id,
              body: c.body,
              maximum: data.isMaximumActive ? c.maximum : null,
            }))
        : undefined;

    const result: CreateQuestionFormData = {
      body: hasFixedQuestionTitle ? (getDefaultQuestionTitle(data.type, data.scope) ?? "") : data.question,
      type: data.type,
      required: data.required,
      choices: cleanedChoices,
    };

    if (formKind === "ticketing") {
      result.scope = data.scope;
      result.askForAllRates = data.askForAllRates;
      result.ratesAssigned = data.ratesAssigned;
      result.isMaximumActive = data.isMaximumActive;
      result.choicesWithMaximum = choicesWithMaximum;
    }

    onSubmit(result);
    resetForm();
    resetScope();
  });

  const handleClose = () => {
    resetForm();
    resetScope();
    resetFundState();
    resetCommunicationPreferencesState();
    onClose();
  };

  const typeConfig = useMemo(() => getQuestionTypeConfig(t), [t]);

  const attendeeNameConfig: QuestionTypeConfig = {
    icon: <User />,
    label: t("dashboard", "stepQuestionsV4.attendeeName"),
  };

  const isFundDesignation = selectedType === FUND_DESIGNATION_TYPE;
  const isCommunicationPreferences = selectedType === COMMUNICATION_PREFERENCES_TYPE;

  const handleFundSubmit = async () => {
    const isRequiredValue = form.getValues("required");
    await handleFundSave(isRequiredValue);
    resetForm();
  };

  const fundDesignationLabel = t("dashboard", "funds.editDesignation.fundDesignation");
  const communicationPreferencesLabel = t("dashboard", "communicationPreferences.responseFormat");

  const dialogActions = isFundDesignation
    ? {
        main: {
          onClick: handleFundSubmit,
          children: t("dashboard", "funds.editDesignation.saveChanges"),
          isLoading: isFundLoading,
          disabled: !hasEnoughFundsSelected,
        },
        cancel: { hidden: false, onClick: handleClose, children: t("dashboard", "donationForm.cancel") },
      }
    : isCommunicationPreferences
      ? {
          main: {
            onClick: handleCommunicationPreferencesSave,
            children: isEditing
              ? t("dashboard", "stepQuestionsV4.builder.saveQuestion")
              : t("dashboard", "stepQuestionsV4.dialog.addQuestion"),
            isLoading: isSavingCommunicationPreferences,
            disabled: !canSaveCommunicationPreferences,
            "data-test": "communication-preferences-submit-button",
          },
          cancel: { hidden: false, onClick: handleClose, children: t("dashboard", "donationForm.cancel") },
        }
      : {
          main: {
            onClick: handleSubmit,
            children: t("dashboard", "stepQuestionsV4.builder.saveQuestion"),
            isLoading,
            "data-test": "v4-submit-question-button",
          },
          cancel: { hidden: false, onClick: handleClose, children: t("dashboard", "donationForm.cancel") },
        };

  const modalTitle = isEditing
    ? t("dashboard", "customQuestion.editTitle")
    : t("dashboard", "stepQuestionsV4.builder.createTitle");

  const questionPlaceholder =
    formKind === "ticketing"
      ? displayedScope === "participant"
        ? selectedType === QuestionType.Multiple
          ? t("dashboard", "stepQuestionsV4.builder.attendeeQuestionPlaceholder")
          : t("dashboard", "customQuestion.placeholder")
        : t("dashboard", "stepQuestionsV4.builder.buyerQuestionPlaceholder")
      : t("dashboard", "customQuestion.placeholder");

  return (
    <>
      <DashboardDialog
        open={isOpen}
        onClose={handleClose}
        title={modalTitle}
        maxWidth="md"
        fullWidth
        transitionDuration={transitionDuration}
        icon={<Add />}
        vibe="brand"
        actions={dialogActions}
        sx={(theme) => ({
          [`& .${zeffyDialogClasses.footer}`]: { borderTop: `1px solid ${theme.palette.border.neutral.quiet}` },
        })}
      >
        <FormProvider {...form}>
          <form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {showScopeSelector && !isFundDesignation && !isSpecialGuestQuestion && (
                <DashboardInputLabel label={getLabel(true, t("dashboard", "stepQuestionsV4.builder.questionAskedTo"))}>
                  <ScopeTogglePills
                    value={displayedScope}
                    onChange={(nextScope) => handleScopeChange(nextScope as QuestionScope)}
                    disabled={
                      selectedType === QuestionType.NotificationPhoneNumber || selectedType === QuestionType.Address
                    }
                    formCategory={formCategory}
                  />
                </DashboardInputLabel>
              )}

              <Stack spacing={2}>
                {!isFundDesignation && !isCommunicationPreferences && !hasFixedQuestionTitle && (
                  <Stack spacing={1}>
                    <DashboardTextField
                      name="question"
                      label={t("dashboard", "customQuestion.label")}
                      placeholder={questionPlaceholder}
                      required
                      control={form.control}
                      rules={[isRequired(t("common", "isRequired")), noDuplicateName]}
                      errorMessage={form.formState.errors.question?.message}
                      data-test="v3-question-body"
                    />

                    {liveMatchedProperty && (
                      <DashboardTypography
                        variant="body2"
                        vibe="text-neutral-quiet"
                        data-test="v4-reused-question-hint"
                      >
                        {t("dashboard", "customQuestion.reuseExistingQuestion")}
                      </DashboardTypography>
                    )}
                  </Stack>
                )}

                <DashboardSelect
                  name="type"
                  label={t("dashboard", "stepQuestionsV4.builder.answerFormat")}
                  defaultValue={QuestionType.Simple}
                  control={form.control}
                  rules={[isRequired(t("common", "isRequired"))]}
                  disabled={
                    (isEditing && disableNonEditableFields) ||
                    selectedType === QuestionType.NotificationPhoneNumber ||
                    selectedType === QuestionType.Address ||
                    isSpecialGuestQuestion ||
                    (Boolean(liveMatchedProperty) && !isMatchFromAutoFilledTitle)
                  }
                  onChange={(e) => {
                    const newType = e.target.value as ResponseFormatType;
                    handleTypeChange(newType);
                    if (newType === COMMUNICATION_PREFERENCES_TYPE) {
                      void ensureDefaultCommunicationPreferenceOption();
                    }
                  }}
                  data-test="v3-question-type"
                  renderValue={(value) => {
                    const config =
                      value === FUND_DESIGNATION_TYPE
                        ? { icon: <Amount />, label: fundDesignationLabel }
                        : value === COMMUNICATION_PREFERENCES_TYPE
                          ? { icon: <Lists />, label: communicationPreferencesLabel }
                          : value === QuestionType.Name && selectedScope === "participant"
                            ? attendeeNameConfig
                            : getQuestionTypeRendererConfig(t, value as QuestionType);
                    if (config) {
                      return (
                        <Stack direction="row" alignItems="center" gap={1}>
                          <DashboardIcon size="small">{config.icon}</DashboardIcon>
                          {config.label}
                        </Stack>
                      );
                    }
                    return String(value);
                  }}
                >
                  {selectableTypes.map((type) => {
                    const config =
                      type === FUND_DESIGNATION_TYPE
                        ? { icon: <Amount />, label: fundDesignationLabel }
                        : type === COMMUNICATION_PREFERENCES_TYPE
                          ? { icon: <Lists />, label: communicationPreferencesLabel }
                          : type === QuestionType.Name && selectedScope === "participant"
                            ? attendeeNameConfig
                            : typeConfig[type as SelectableQuestionType];
                    return (
                      <DashboardMenuItem
                        key={type}
                        value={type}
                        startIcon={<DashboardIcon size="small">{config.icon}</DashboardIcon>}
                      >
                        {config.label}
                      </DashboardMenuItem>
                    );
                  })}
                </DashboardSelect>

                {isCommunicationPreferences ? (
                  <CommunicationPreferencesFormContent
                    options={communicationPreferenceOptions}
                    selectedContactListIds={selectedCommunicationPreferenceIds}
                    questionTitle={communicationPreferencesQuestionTitle}
                    defaultQuestionTitle={defaultCommunicationPreferencesQuestionTitle}
                    isLoading={isLoadingCommunicationPreferences}
                    canManageOptions={canManageCommunicationPreferenceOptions}
                    formType={communicationPreferenceFormType}
                    onSelectedContactListIdsChange={setSelectedCommunicationPreferenceIds}
                    onQuestionTitleChange={setCommunicationPreferencesQuestionTitle}
                    onCreateOption={handleCreateCommunicationPreferenceOption}
                    onRenameOption={handleRenameCommunicationPreferenceOption}
                    onDeleteOption={handleDeleteCommunicationPreferenceOption}
                  />
                ) : isFundDesignation ? (
                  <>
                    <FundDesignationFormContent
                      locale={locale}
                      selectedFundIds={selectedFundIds}
                      onSelectedFundIdsChange={setSelectedFundIds}
                    />

                    <DashboardSwitch
                      name="required"
                      label={t("dashboard", "form.requiredQuestion")}
                      checked={form.watch("required")}
                      onChange={(_, checked) => form.setValue("required", checked)}
                      data-test="v3-question-required"
                    />
                  </>
                ) : (
                  <>
                    <CollapseAndFade in={needsChoices} unmountOnExit>
                      <ChoicesSection
                        name="choices"
                        control={form.control}
                        errors={form.formState.errors}
                        isRequired={true}
                        minimumChoicesRequired={minimumChoicesRequired}
                        isMaximumActive={isMaximumActive}
                        isMaximumEnabled={showMaximumLimit}
                      />
                    </CollapseAndFade>

                    <Stack spacing={2}>
                      <CollapseAndFade in={shouldShowRateAssignment} unmountOnExit>
                        <RateAssignmentSection rates={rates} formCategory={formCategory} />
                      </CollapseAndFade>

                      <CollapseAndFade in={shouldShowMaxChoices} unmountOnExit>
                        <DashboardSwitch
                          name="isMaximumActive"
                          label={t("dashboard", "stepQuestionsV4.builder.maxChoiceLimit")}
                          checked={isMaximumActive}
                          onChange={(_, checked) => form.setValue("isMaximumActive", checked)}
                        />
                      </CollapseAndFade>

                      <DashboardSwitch
                        name="required"
                        label={t("dashboard", "form.requiredQuestion")}
                        checked={form.watch("required")}
                        onChange={(_, checked) => form.setValue("required", checked)}
                        data-test="v3-question-required"
                      />
                    </Stack>
                  </>
                )}
              </Stack>
            </Stack>
          </form>
        </FormProvider>
      </DashboardDialog>

      <DashboardAdaptiveModal
        isOpen={pendingScopeChange !== null}
        onClose={() => setPendingScopeChange(null)}
        title={
          <DashboardTypography variant="h6">
            {t("dashboard", "liveFormEditor.stepQuestions.changeScopeConfirmation.title")}
          </DashboardTypography>
        }
        actions={{
          main: {
            vibe: "danger",
            children: t("dashboard", "liveFormEditor.stepQuestions.changeScopeConfirmation.confirm"),
            onClick: () => {
              if (pendingScopeChange) {
                commitScopeChange(pendingScopeChange);
              }
              setPendingScopeChange(null);
            },
          },
          cancel: {
            vibe: "neutral",
            children: t("dashboard", "donationForm.cancel"),
            onClick: () => setPendingScopeChange(null),
          },
        }}
        noDivider
      >
        <Box width="100%">
          <Trans
            space="dashboard"
            i18nKey="liveFormEditor.stepQuestions.changeScopeConfirmation.body"
            values={{ count: countAlreadyAnswered }}
            components={{ bold: <DashboardTypography component="span" variant="subtitle2" /> }}
          />
        </Box>
      </DashboardAdaptiveModal>
    </>
  );
};
